import { useQuery } from '@tanstack/react-query';
import { ApiError } from 'fetcher-session';
import { Form, Formik } from 'formik';
import { FormikField, FormikSelectField } from 'formik-ui';
import React, { useMemo, useState } from 'react';
import { Button } from 'ui';
import { displayPrice } from 'utils';
import * as yup from 'yup';
import { fetchRegions } from '~/api/address';
import { fetchItemShipping } from '~/api/item';
import Tooltip from '~/components/Form/Tooltip';
import HelpButton from '~/components/Links/HelpButton';
import HelpLink from '~/components/Links/HelpLink';
import NewTabLink from '~/components/Links/NewTabLink';
import SimpleModal from '~/components/NewModal/SimpleModal';
import { useSession } from '~/contexts/SessionContext';
import useAddress from '~/hooks/useAddress';
import useKnownCookie from '~/hooks/useKnownCookie';
import {
  RailsItem,
  RailsShipping,
  RailsThumbnailItem,
} from '~/typings/services/rails/item';

interface Props {
  item: RailsItem | RailsThumbnailItem;
}

const CalculateItemShipping: React.FC<Props> = ({ item }) => {
  // const router = useRouter();
  const { user, loggedIn } = useSession();
  const [modalOpen, setModalOpen] = useState(false);
  const [shipToCountryCookie, setShipToCountryCookie] =
    useKnownCookie('ship_to_country');
  const [shipToZipCookie, setShipToZipCookie] = useKnownCookie('ship_to_zip');

  const shouldFetchShipping =
    item.state === 'available' &&
    (user ? user.username !== item.seller.username : true);

  const { data: shipToAddress, isLoading: isAddressesFetching } =
    useAddress('shipTo');

  const dataSource = shipToAddress
    ? { ship_to_address_id: shipToAddress.id }
    : {
        zip: shipToZipCookie!,
        country: shipToCountryCookie!,
      };

  const {
    error,
    data: shipping,
    isFetching: isShippingFetching,
    refetch,
  } = useQuery<RailsShipping, ApiError>(
    ['shipping', item.id, dataSource],
    () => fetchItemShipping(item.id, dataSource),
    {
      enabled: false, // only fetched on demand
      staleTime: Infinity,
      retry: false,
    },
  );

  const { data: regions } = useQuery(['regions'], fetchRegions, {
    enabled: modalOpen,
  });
  const sortedRegions = useMemo(
    () => regions?.sort((a, b) => a.name.localeCompare(b.name)),
    [regions],
  );

  // useEffect(() => {
  //   if (
  //     item &&
  //     router.route === '/gear/[...item]' &&
  //     router.query.action === 'calculate_shipping'
  //   ) {
  //     setModalOpen(true);
  //     router.replace(router.route, router.asPath.split('?')[0], {
  //       shallow: true,
  //     });
  //   }
  // }, [item?.id]);

  let content: React.ReactNode = null;

  if (!shouldFetchShipping) {
    return null;
  }

  if (error) {
    content = (
      <div className="text-slate-green-500 flex items-center self-center text-xs font-normal">
        Unable to ship to your address{' '}
        {!shipToAddress && (
          <button
            type="button"
            className="text-slate-green-500 ml-2 text-xs underline"
            onClick={() => setModalOpen(true)}
          >
            Edit
          </button>
        )}
        <div className="mx-2 self-center">
          <Tooltip
            direction="top"
            width={300}
            tooltip={
              <div>
                Shipments to your location from this seller are disabled. Please
                contact support if you think this message in surfacing in error.
                <NewTabLink
                  href="https://help.sidelineswap.com/hc/en-us/articles/360014263019-Why-can-t-I-see-a-shipping-price-on-this-item"
                  title="Learn more about why you can't see shipping on this item."
                >
                  Learn more
                </NewTabLink>
              </div>
            }
          >
            <HelpButton />
          </Tooltip>
        </div>
      </div>
    );
  } else if (
    (!shipToAddress && isAddressesFetching && loggedIn) ||
    (!shipping && isShippingFetching)
  ) {
    if (isShippingFetching) {
      content = (
        <div className="text-slate-green-500 flex-shrink-0 self-center text-xs font-normal">
          Calculating...
        </div>
      );
    } else {
      content = null;
    }
  } else if (shipping) {
    content = (
      <>
        <div className="flex-shrink-0 self-center text-sm font-normal">
          {shipping.label ||
            (shipping.shipping === 0
              ? 'Free Shipping'
              : `+ ${displayPrice(shipping.shipping)} Shipping`)}{' '}
          {!shipToAddress && (
            <button
              type="button"
              className="text-slate-green-500 ml-2 text-xs underline"
              onClick={() => setModalOpen(true)}
            >
              Edit
            </button>
          )}
        </div>

        <div className="mx-2 self-center">
          <Tooltip
            direction="top"
            tooltip={
              <div>
                <p>
                  <b>
                    Ships from{' '}
                    {shipping.ships_from_region || shipping.ships_from}
                  </b>
                </p>
                {(shipping.carrier || shipping.service) && (
                  <p>
                    {shipping.carrier} {shipping.service}
                  </p>
                )}
                {shipping.ships_from === 'US' && (
                  <p>
                    <HelpLink
                      className="text-blue-500 underline"
                      link="shippingRegions"
                    >
                      Learn more
                    </HelpLink>
                  </p>
                )}
              </div>
            }
          >
            <HelpButton />
          </Tooltip>
        </div>
      </>
    );
  } else if (
    !shipping &&
    (shipToAddress || shipToCountryCookie || shipToZipCookie)
  ) {
    content = (
      <button
        type="button"
        className="flex-shrink-0 self-center text-sm font-normal underline"
        onClick={() => refetch()}
      >
        Calculate Shipping
      </button>
    );
  } else if (!shipToAddress || !shipToCountryCookie || !shipToZipCookie) {
    content = (
      <button
        type="button"
        className="flex-shrink-0 self-center text-sm font-normal underline"
        onClick={() => setModalOpen(true)}
      >
        Calculate Shipping
      </button>
    );
  } else {
    content = (
      <div className="flex-shrink-0 self-center text-sm font-normal">
        + Shipping
      </div>
    );
  }

  return (
    <React.Fragment>
      {content}

      <SimpleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Calculate Shipping"
        description="Enter the information below to get estimated shipping."
      >
        <Formik
          initialValues={{
            country: shipToCountryCookie || 'US',
            zip: shipToZipCookie,
          }}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={yup.object().shape({
            country: yup.string().required('Required'),
            zip: yup.string().required('Required'),
          })}
          onSubmit={values => {
            setShipToCountryCookie(values.country!, { expires: 365 });
            setShipToZipCookie(values.zip!, { expires: 365 });
            setTimeout(() => refetch());
            setModalOpen(false);
          }}
        >
          <Form>
            <FormikSelectField label="Country" name="country">
              {sortedRegions?.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </FormikSelectField>

            <FormikField
              name="zip"
              label="Zip"
              style={{
                width: 110,
              }}
            />

            <Button color="turf-green" type="submit" className="w-full">
              Calculate Shipping
            </Button>
          </Form>
        </Formik>
      </SimpleModal>
    </React.Fragment>
  );
};

export default CalculateItemShipping;
